import { escapeHtml } from '../ui.js';
import { field, primaryButton, secondaryButton, cancelButton, setPrimaryState, participle, injectFormStyles, closeFieldMenu, fieldMenuOpen } from './forms.js';

// ─────────────────────────────────────────────────────────────────────────────
// The canonical dialog shell — handoff-dialogs-forms, artboards 7a/7b/7f/7g.
//
// The surface test (7a) governs which of these you reach for:
//   • openDialog  → a MODAL (520 or 640), CENTRED, for CREATING a record.
//                   A modal that edits an existing record is a bug in this
//                   system, not a variant of it (7a).
//   • openPanel   → a 520 PANEL from the right, full height, for editing several
//                   fields of a record that EXISTS. Full geometry in 6d.
//
// Card (7b): 520 (or 640) wide, max-height min(680px, 100vh - 96px), radius 20,
// padding 28px 30px 26px, shadow 0 24px 60px rgba(12,17,57,0.3), centred both
// axes, scrim rgba(12,17,57,0.42) with no blur, ever.
//
// The scroll condition (7b/7f): when the body clips, the header and footer PIN
// and each takes a 1px --brand-grey-100 line. A form that fits has no lines and
// no pinning. Same component, one condition.
//
// This module lays a form out and wires validation, Enter/Escape, the focus
// trap and the motion. It does NOT write — spec.primary.onSubmit(values, api)
// does, exactly as the edit-patterns components leave the write to the caller.
// ─────────────────────────────────────────────────────────────────────────────

function injectDialogStyles() {
  if (document.getElementById('tf-dialog-styles')) return;
  const s = document.createElement('style');
  s.id = 'tf-dialog-styles';
  s.textContent = `
  /* The safe-area insets are ADDED to the padding rather than replacing it:
     in standalone, viewport-fit=cover means inset:0 is the top of the screen,
     so a dialog centred in 48px of padding still has its head under the clock
     on a notched phone. Every create and edit in Track opens in this. */
  .tf-scrim { position:fixed; inset:0; z-index:1100; background:rgba(var(--scrim-rgb), 0.42);
    display:flex; align-items:center; justify-content:center;
    padding:calc(48px + env(safe-area-inset-top,0px)) 24px calc(48px + env(safe-area-inset-bottom,0px));
    animation:tf-scrim-in 200ms ease-out; }
  .tf-scrim.tf-panel-scrim { justify-content:flex-end;
    padding:env(safe-area-inset-top,0px) 0 env(safe-area-inset-bottom,0px); }
  @keyframes tf-scrim-in { from { opacity:0; } to { opacity:1; } }

  /* The confirm dialog's width lives here, NOT inline: an inline style beats
     the 760px rule below, so it used to overflow a phone (audit, 28/08). */
  .tf-confirm { width:480px; }
  .tf-card { box-sizing:border-box; background:var(--surface); border-radius:16px;
    box-shadow:0 24px 60px rgba(12,17,57,0.3); display:flex; flex-direction:column;
    max-height:min(680px, calc(100 * var(--dvh)) - 96px); overflow:hidden;
    animation:tf-card-in 160ms ease-out; }
  @keyframes tf-card-in { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:none; } }
  .tf-card.tf-out { animation:tf-card-out 120ms ease-out forwards; }
  @keyframes tf-card-out { to { opacity:0; } }
  .tf-scrim.tf-out { animation:tf-scrim-out 120ms ease-out forwards; }
  @keyframes tf-scrim-out { to { opacity:0; } }

  /* Panel (7g): 520 from the right, full height, 1px --brand-grey-200 left border,
     -18px 0 48px rgba(12,17,57,0.18). Footer always pins. */
  .tf-card.tf-panel { border-radius:0; height:calc(100 * var(--dvh)); max-height:calc(100 * var(--dvh)); width:520px;
    border-left:1px solid var(--hair); box-shadow:-18px 0 48px rgba(12,17,57,0.18);
    animation:tf-panel-in 240ms ease-out; }
  @keyframes tf-panel-in { from { transform:translateX(100%); } to { transform:none; } }

  .tf-head { flex:none; padding:24px 24px 8px; display:flex; align-items:flex-start; gap:16px; }
  .tf-head.tf-pinned { border-bottom:1px solid var(--hair-soft); }
  .tf-eyebrow { font-size:12px; font-weight:400; letter-spacing:0; text-transform:none; color:var(--ink-3); margin-bottom:6px; }
  .tf-title { font-size:20px; font-weight:500; letter-spacing:-0.01em; color:var(--ink); }
  .tf-subtitle { margin-top:7px; font-size:13.5px; line-height:1.5; color:var(--ink-3); }
  .tf-lastedit { margin-top:7px; font-size:12px; color:var(--ink-3); }
  .tf-close { width:28px; height:28px; flex:none; border:none; background:none; border-radius:8px; margin:-2px -6px 0 0;
    display:flex; align-items:center; justify-content:center; font-size:15px; color:var(--ink-3); cursor:pointer;
    transition:background 120ms ease, color 120ms ease; }
  .tf-close:hover { background:var(--nav-hover); color:var(--ink); }
  .tf-head-aside { flex:none; align-self:center; font-size:12.5px; color:var(--ink-2); margin-top:-2px; }

  .tf-body { flex:1 1 auto; min-height:0; overflow-y:auto; padding:16px 24px 24px; }
  .tf-grid { display:grid; grid-template-columns:1fr 1fr; gap:18px 16px; align-content:start; }
  .tf-section + .tf-section { margin-top:24px; }
  .tf-section-eyebrow { font-size:13px; font-weight:500; letter-spacing:0; text-transform:none;
    color:var(--ink); margin-bottom:12px; }

  /* Consequence note (7g) — --brand-blue-50, above the footer, when a change
     affects records that already exist. */
  .tf-conseq { flex:none; margin:0 30px; padding:12px 14px; background:var(--nav-hover); border-radius:10px;
    font-size:12.5px; line-height:1.45; color:var(--anchor); }

  .tf-foot { flex:none; padding:16px 24px calc(16px + env(safe-area-inset-bottom));
    display:flex; align-items:center; gap:10px; border-top:1px solid var(--hair-soft); }
  .tf-foot.tf-pinned { border-top:1px solid var(--hair-soft); }
  .tf-foot .tf-spacer { flex:1; }
  .tf-note { font-family:var(--font-sans); font-size:12px; line-height:1.4; color:var(--ink-3); text-align:left; max-width:280px; }
  .tf-note.tf-note-err { font-family:var(--font-sans); color:var(--brand-orange); }

  /* 760px, matching the shell's breakpoint (mobile canvas 2a): below it the
     tab bar is showing, so a dialog must be a sheet too. */
  @media (max-width:760px) {
    .tf-scrim { padding:0; align-items:stretch; }
    .tf-card, .tf-card.tf-panel, .tf-card.tf-confirm { width:100%; max-width:100%; height:calc(100 * var(--dvh)); max-height:calc(100 * var(--dvh)); border-radius:0; border-left:none; }
    .tf-grid { grid-template-columns:1fr; }
  }`;
  document.head.appendChild(s);
}

// Normalise fields | sections into a flat spec list + a renderer.
function normaliseSections(spec) {
  if (spec.sections) return spec.sections.map((s) => ({ eyebrow: s.eyebrow, fields: s.fields || [] }));
  return [{ eyebrow: null, fields: spec.fields || [] }];
}

function buildBody(bodyEl, sections, controls) {
  sections.forEach((sec) => {
    const secEl = document.createElement('div');
    secEl.className = 'tf-section';
    if (sec.eyebrow) {
      const eb = document.createElement('div');
      eb.className = 'tf-section-eyebrow';
      eb.textContent = sec.eyebrow;
      secEl.appendChild(eb);
    }
    const grid = document.createElement('div');
    grid.className = 'tf-grid';
    sec.fields.forEach((fs) => {
      const ctl = field(fs);
      if (fs.key) controls.set(fs.key, ctl);
      grid.appendChild(ctl.el);
    });
    secEl.appendChild(grid);
    bodyEl.appendChild(secEl);
  });
}

// A dialog never opens a dialog. If a create form needs a record that does not
// exist, its select gains an inline "add new" row (7a) — handled in forms.js.
// The 480 "discard?" ask when Escape is pressed on a dirty form (7e).
function askDiscard(onDiscard) {
  injectDialogStyles();
  const s = document.createElement('div');
  s.className = 'tf-scrim';
  s.style.zIndex = '1200';
  s.innerHTML = `
    <div class="tf-card tf-confirm" role="dialog" aria-modal="true" style="padding:28px 30px 26px;max-height:none;">
      <div class="tf-title">Discard your changes?</div>
      <div style="margin-top:12px;font-size:13.5px;line-height:1.5;color:var(--ink-2);">You've started filling this in. Leaving now loses what you've typed.</div>
      <div style="margin-top:24px;display:flex;align-items:center;gap:14px;">
        <button type="button" class="tf-btn tf-btn-primary" data-keep>Keep editing</button>
        <button type="button" class="tf-btn tf-btn-cancel" data-discard style="color:var(--brand-orange);">Discard</button>
      </div>
    </div>`;
  document.body.appendChild(s);
  const close = () => s.remove();
  s.querySelector('[data-keep]').addEventListener('click', close);
  s.querySelector('[data-discard]').addEventListener('click', () => { close(); onDiscard(); });
  s.addEventListener('mousedown', (e) => { if (e.target === s) close(); });
  s.querySelector('[data-keep]').focus();
}

/**
 * The ask before something is lost for good. Resolves true only for the explicit
 * destructive button: Stay has the focus, and Escape or a click outside are Stay.
 */
export function askBeforeLosing({ title, body, stay, go }) {
  injectFormStyles();
  injectDialogStyles();
  return new Promise((resolve) => {
    const s = document.createElement('div');
    s.className = 'tf-scrim tf-loss';
    s.style.zIndex = '1500';             // above the drawing viewer too
    s.innerHTML = `
      <div class="tf-card tf-confirm" role="alertdialog" aria-modal="true" style="padding:28px 30px 26px;max-height:none;">
        <div class="tf-title">${escapeHtml(title)}</div>
        <div style="margin-top:12px;font-size:13.5px;line-height:1.5;color:var(--ink-2);">${escapeHtml(body)}</div>
        <div style="margin-top:24px;display:flex;align-items:center;gap:14px;flex-wrap:wrap;">
          <button type="button" class="tf-btn tf-btn-primary" data-stay>${escapeHtml(stay)}</button>
          <button type="button" class="tf-btn tf-btn-cancel" data-go style="color:var(--brand-orange);">${escapeHtml(go)}</button>
        </div>
      </div>`;
    document.body.appendChild(s);
    const done = (out) => { document.removeEventListener('keydown', onKey, true); s.remove(); resolve(out); };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); done(false); } };
    document.addEventListener('keydown', onKey, true);
    s.querySelector('[data-stay]').addEventListener('click', () => done(false));
    s.querySelector('[data-go]').addEventListener('click', () => done(true));
    s.addEventListener('mousedown', (e) => { if (e.target === s) done(false); });
    s.querySelector('[data-stay]').focus();
  });
}

// ── openDialog — the create modal (520 / 640) ────────────────────────────────
export function openDialog(spec = {}) {
  injectFormStyles();
  injectDialogStyles();
  closeFieldMenu();
  document.querySelector('.tf-scrim')?.remove();

  // 760: a create form with a calendar beside it (New request, v4.5.0).
  // 960: a create form with the source document beside it (Add expense, receipt first, v4.6.0).
  const width = [640, 760, 960].includes(spec.width) ? spec.width : 520;
  const sections = normaliseSections(spec);
  const controls = new Map();

  const panelMode = !!spec.__panel;
  const scrim = document.createElement('div');
  scrim.className = 'tf-scrim' + (panelMode ? ' tf-panel-scrim' : '');
  const card = document.createElement('div');
  card.className = 'tf-card' + (panelMode ? ' tf-panel' : '') + (spec.cardClass ? ` ${spec.cardClass}` : '');
  if (!panelMode) card.style.width = width + 'px';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');

  // Header
  const head = document.createElement('div');
  head.className = 'tf-head';
  head.innerHTML = `
    <div style="flex:1;min-width:0;">
      ${spec.eyebrow ? `<div class="tf-eyebrow">${escapeHtml(spec.eyebrow)}</div>` : ''}
      <div class="tf-title">${escapeHtml(spec.title || '')}</div>
      ${spec.subtitleHtml ? `<div class="tf-subtitle">${spec.subtitleHtml}</div>` : spec.subtitle ? `<div class="tf-subtitle">${escapeHtml(spec.subtitle)}</div>` : ''}
      ${spec.lastEdited ? `<div class="tf-lastedit">${escapeHtml(spec.lastEdited)}</div>` : ''}
    </div>
    ${spec.headAside ? `<div class="tf-head-aside">${escapeHtml(spec.headAside)}</div>` : ''}
    <button type="button" class="tf-close" aria-label="Close">×</button>`;

  // Body
  const body = document.createElement('div');
  body.className = 'tf-body';
  if (typeof spec.render === 'function') {
    spec.render(body, { field, controls });
  } else {
    buildBody(body, sections, controls);
  }

  // Consequence note (7g) — only on a panel/edit surface; created if requested.
  let conseqEl = null;
  if (spec.consequence) {
    conseqEl = document.createElement('div');
    conseqEl.className = 'tf-conseq';
    conseqEl.textContent = spec.consequence;
  }

  // Footer (7e): primary, Cancel 14px right of it, then the space, then the note.
  const foot = document.createElement('div');
  foot.className = 'tf-foot';
  // A VIEW panel (spec.view) reads a record and writes nothing itself: no
  // Save/Cancel, and Enter is left to the focused link or button. The caller
  // fills the footer through the returned `foot` (before the note).
  const viewMode = !!spec.view;
  // v4 (p51/p52): the note (or hint) on the left, then Cancel, then the primary on the right.
  const primary = primaryButton(spec.primary?.label || 'Save');
  const note = document.createElement('div'); note.className = 'tf-note'; note.textContent = spec.note || ''; foot.appendChild(note);
  const spacer = document.createElement('div'); spacer.className = 'tf-spacer'; foot.appendChild(spacer);
  const cancel = cancelButton(spec.cancelLabel || 'Cancel');   // e.g. 'Skip' (Add the chip ID)
  if (!viewMode) foot.appendChild(cancel);
  let secondary = null;
  if (spec.secondary && !viewMode) { secondary = secondaryButton(spec.secondary.label); foot.appendChild(secondary); }
  if (!viewMode) foot.appendChild(primary);

  card.appendChild(head);
  card.appendChild(body);
  if (conseqEl) card.appendChild(conseqEl);
  card.appendChild(foot);
  scrim.appendChild(card);
  document.body.appendChild(scrim);

  // The api handed to onSubmit / onClick — the caller drives states + notes.
  let closed = false;
  const api = {
    controls,
    get: (k) => controls.get(k)?.get(),
    values() { const o = {}; controls.forEach((c, k) => { o[k] = c.get(); }); return o; },
    setNote(text, isErr) { note.textContent = text || ''; note.classList.toggle('tf-note-err', !!isErr); },
    setPrimary(state) { setPrimaryState(primary, state); },
    fieldError(key, msg) { const c = controls.get(key); if (c) c.setError(msg); },
    close: () => doClose(true),
  };

  const doClose = (force) => {
    if (closed) return;
    closed = true;
    closeFieldMenu();
    card.classList.add('tf-out');
    scrim.classList.add('tf-out');
    document.removeEventListener('keydown', onKey, true);
    setTimeout(() => scrim.remove(), 120);
  };

  // Dirty = changed from what the form OPENED with, not "has a value": an edit
  // panel opens filled in, and closing it untouched must not ask (Karan 17/10,
  // Suppliers). A create form opens empty, so typing anything still counts.
  const valueKey = (v) => (v === '' || v == null || v === false ? '' : JSON.stringify(v));
  const opened = new Map();
  controls.forEach((c, k) => { if (!c.readonly) opened.set(k, valueKey(c.get())); });
  const isDirty = () => {
    let dirty = false;
    controls.forEach((c, k) => { if (c.readonly) return; if (valueKey(c.get()) !== (opened.get(k) ?? '')) dirty = true; });
    return dirty;
  };
  const attemptClose = () => {
    if (isDirty()) askDiscard(() => doClose(true)); else doClose(true);
    if (typeof spec.onCancel === 'function') { /* fired only on real close */ }
  };

  // Submit — validate all; on failure move focus to the first field in error and
  // replace the footer note with the count (7g).
  const submit = async () => {
    if (primary.disabled) return;
    let firstBad = null, bad = 0;
    controls.forEach((c) => { if (c.readonly) return; if (!c.validate()) { bad++; if (!firstBad) firstBad = c; } });
    if (bad) { firstBad?.focus(); api.setNote(`${bad} field${bad === 1 ? '' : 's'} need${bad === 1 ? 's' : ''} attention`, true); return; }
    api.setNote(spec.note || '');
    setPrimaryState(primary, 'working');
    if (secondary) setPrimaryState(secondary, 'notyet'); // writes are exclusive — hold the other button
    try {
      await spec.primary?.onSubmit?.(api.values(), api);
      doClose(true);
    } catch (e) {
      setPrimaryState(primary, 'rest');
      if (secondary) setPrimaryState(secondary, 'rest');
      api.setNote(e?.message || String(e), true);
    }
  };
  primary.addEventListener('click', submit);
  if (secondary) secondary.addEventListener('click', async () => {
    // Guard BOTH buttons: while either save is in flight the other must not
    // fire — primary and secondary often run the same write (e.g. the + Add
    // enquiry draft), and Enter also triggers primary (Karan 19/08).
    if (secondary.disabled || primary.disabled) return;
    setPrimaryState(secondary, 'working');
    setPrimaryState(primary, 'notyet');
    try { await spec.secondary.onClick?.(api.values(), api); doClose(true); }
    catch (e) {
      setPrimaryState(secondary, 'rest');
      setPrimaryState(primary, 'rest');
      api.setNote(e?.message || String(e), true);
    }
  });
  cancel.addEventListener('click', attemptClose);
  head.querySelector('.tf-close').addEventListener('click', attemptClose);
  scrim.addEventListener('mousedown', (e) => { if (e.target === scrim) attemptClose(); });

  // Keyboard (7b/7e): Enter fires primary from any field except the textarea;
  // Escape closes (asking once if dirty); Tab is trapped inside the card.
  const onKey = (e) => {
    // An open project picker (project-picker.js) owns Esc/Enter/Tab: Esc closes
    // the list, not the dialog; the phone sheet sits outside the card.
    const pt = e.target && e.target.closest ? e.target.closest('.pp-open, .pp-sheet') : null;
    if (pt && ['Escape', 'Enter', 'Tab'].includes(e.key)) return;
    // An open select/date menu (forms.js) owns Escape: it closes the menu, not the dialog.
    if (e.key === 'Escape' && fieldMenuOpen()) return;
    if (e.key === 'Escape') { e.preventDefault(); attemptClose(); return; }
    if (e.key === 'Enter' && !e.shiftKey && !viewMode) {
      const t = e.target;
      const inTextarea = t && t.tagName === 'TEXTAREA';
      const inMenu = t && t.closest && t.closest('.tf-menu');
      if (!inTextarea && !inMenu && card.contains(t)) { e.preventDefault(); submit(); }
      return;
    }
    if (e.key === 'Tab') trapTab(e, card);
  };
  document.addEventListener('keydown', onKey, true);

  // Focus first field on open, after the entrance completes (7b). Then apply the
  // scroll condition once laid out.
  requestAnimationFrame(() => {
    applyScrollCondition(body, head, foot, panelMode);
    setTimeout(() => {
      const first = [...controls.values()].find((c) => !c.readonly);
      first?.focus();
    }, 170);
  });

  return { scrim, card, body, foot, close: () => doClose(true), api, controls };
}

// ── openPanel — the 520 edit surface (7g / 6d) ───────────────────────────────
// The panel is the same form re-flowed: 520 from the right, footer pins always
// (a panel scrolls eventually), a changed field holds its blue border/halo, and
// a consequence note sits above the footer when the change touches existing
// records. Provided here so create-vs-edit share one form engine; the existing
// 6d person/project panels predate it and are migrated separately.
export function openPanel(spec = {}) {
  const d = openDialog({ ...spec, __panel: true });
  return d;
}

// Header/footer pin + hairline only when the body can scroll (7b/7f). A form that
// fits has neither. Panels always pin their footer (handled by caller flag).
function applyScrollCondition(body, head, foot, alwaysFoot) {
  const clips = body.scrollHeight > body.clientHeight + 1;
  head.classList.toggle('tf-pinned', clips);
  foot.classList.toggle('tf-pinned', clips || !!alwaysFoot);
}

// Keep Tab inside the card (7b).
function trapTab(e, card) {
  const focusable = card.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])');
  const list = [...focusable].filter((el) => el.offsetParent !== null);
  if (!list.length) return;
  const first = list[0], last = list[list.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}

export { participle };
